import React, { useState, useEffect, lazy, Suspense } from 'react';
import * as api from '../../utils/api';
import { useParams, useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import parse from 'html-react-parser';
import Tooltip from '../Tooltip';
import LoadingSpinner from '../LoadingSpinner';
import { TAnswers, TAllData, TParams, TSnack, TCalculatorContent } from '../../utils/types';
import ButtonContainer from '../ButtonContainer';
import Grid from '../Grid';
import Calculator from '../Calculator';
import Accordion from '../Accordion';

const Counter = lazy(() => import('../Counter'));
const Card = lazy(() => import('../Card'));
const Overlay = lazy(() => import('../Overlay'));

export interface SnackProps {
  setNavParams: (params:TParams)=>void
  navParams: TParams
  btnText: string
  answers: TAnswers[]
  allData: TAllData
  servicetype: string
  resultUrl: string
  pensionsAlder: number
}

const MotionGrid = motion(Grid);

const Snack = ({ setNavParams, navParams, btnText, answers, allData, servicetype, resultUrl, pensionsAlder }: SnackProps) => {
	const [data, setData] = useState<TSnack>();
	const [isLoading, setIsLoading] = useState(true);
	const { chapterId, pageId } = useParams<{chapterId: string, pageId: string}>();
	const history = useHistory();
	const [calculatorContent, setCalculatorContent] = React.useState<TCalculatorContent>();
	const [calculatorLoading, setCalculatorLoading] = React.useState(false);
	const [calculatorValues, setCalculatorValues] = React.useState<{Procent: number, Ydelse: number}>();
	const [showContent, setShowContent] = React.useState<string|null>(null);
	const nextChapter = Number(chapterId) + 1;
	const { variant, chapterTitle } = navParams;
	const lastChapter = allData.Chapters.length;
	const episerverId = allData.Chapters[Number(chapterId) - 1].EpiserverId;
	const hasCalculator = allData.Chapters[Number(chapterId) - 1].SparMereOpBeregner;

	useEffect(() => {
		fetchSnack();
	}, []);

	const fetchSnack = async () => {
		if (!episerverId) {
			return;
		}
		setIsLoading(true);
		const data = await api.getSnack(episerverId, servicetype, answers);

		setData(data);
		setIsLoading(false);
	};

	const handleAccordionClick = (id: string) => {
		setShowContent(showContent === id ? null : id);
	};

	const getBeregnerContent = async () => {
		try {
			const content = await api.getBeregnerContent(episerverId, false) as TCalculatorContent;

			if (content && content.Text !== null && content.Title != null && content.Ydelse > 0) {
				setCalculatorContent(content as TCalculatorContent);

				if (!calculatorValues) {
					setCalculatorValues({ Procent: content.Procent, Ydelse: content.Ydelse });
				}
			}
		} catch (error) {
			console.error(error);
		}
	};

	const doCalculation = async (_value: number, abortSignal: AbortSignal) => {
		setCalculatorLoading(true);
		try {
			const values = await api.beregnSparMereOp(_value, abortSignal);

			if (values) {
				setCalculatorValues(values as {Procent: number, Ydelse: number});
			}
		} catch (error) {
			console.error(error);
		}
		setCalculatorLoading(false);
	};

	useEffect(()=> {
		if (!isLoading) {
			if (hasCalculator){
				getBeregnerContent();
			}
		}
	}, [data]);

	useEffect(()=> {
		setNavParams({
			chapter: chapterId,
			chapterTitle,
			page: 'snack',
			show: true,
			// prev: `/kapitel/${chapterId}/side/${pageId}`,
			prev: (!pageId || pageId === '0') ? `/kapitel/${chapterId}` : `/kapitel/${chapterId}/side/${pageId}`,
			variant
		});
	}, [nextChapter, chapterId, pageId, variant]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		Number(chapterId) !== lastChapter ? history.push(`/kapitel/${nextChapter}`) : (servicetype === 'Velkomst' ? history.push('/anbefalinger') : window.location.href = resultUrl);
	};

	if (isLoading) {
		return <LoadingSpinner />;
	}

	const renderCards = () => {
		return data?.Cards?.map((card, i) => {
			return <Card key={ i } cardData={ { ...card } } />;
		});
	};

	const renderAccordion = () => {
		return data?.Accordions?.map((e, i) => {
			return <Accordion key={ i } id={ i } headline={ e.Title } paragraph={ e.Text } showContent={ showContent } handleAccordionClick={ handleAccordionClick } />;
		});
	};

	return (
		<MotionGrid
			className="page"
			mobileBlock
			initial={ { opacity: 0, x: 0, y: navParams.variant === 'down' ? 700 : -700 } }
			animate={ { opacity: 1, x: 0, y: 0 } }
			exit={ { opacity: 0, x: 0, y: navParams.variant === 'down' ? -700 : 700 } }
			transition={ { duration: 0.5, ease: 'easeIn' } }
		>
			<Suspense fallback={ <LoadingSpinner /> }>
				<Grid.Element position="left" type="text" className="page-info">
					<span>{ chapterId }</span>
					<span>{ chapterTitle }</span>
				</Grid.Element>
				<Grid.Element type="text" position="left" customStyle={ { marginTop: '2rem' } }>
					<form method="post" onSubmit={ handleSubmit }>
						<div className="page-content">
							{ data.IllustrationAnvendes && pensionsAlder && <Counter to={ pensionsAlder } /> }
							{ data?.Text && <span>{ parse(data.Text) }</span> }
							{ data?.Tooltip && <Tooltip tooltipData={ data.Tooltip } /> }
						</div>
						{ data?.OverlayData &&
							<Overlay overlayData={ data.OverlayData } overlayTrigger={ data.OverlayTrigger || '' } /> }
						{ data?.Cards && <div className="lhp-cards">{ renderCards() }</div> }
						{ data?.Accordions && data?.Accordions.length > 0 && <div className="accordions">{ renderAccordion() }</div> }
						<ButtonContainer>
							<button type="submit" className="btn">
								{ btnText }
							</button>
							{ data?.ButtonLink && (
								<a href={ data.ButtonLink } className="btn btn-secondary second-btn" target="_blank" rel="noreferrer">
									{ data.ButtonText }
								</a>
							) }
						</ButtonContainer>
						<motion.button
							type="submit"
							className="btn-nav-down"
							onClick={ () => setNavParams({ ...navParams, variant: 'down' }) }
							initial={ { opacity: 0 } }
							animate={ { opacity: 1 } }
							transition={ { duration: 1.5, ease: 'easeIn' } }
						>
							<svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="21" cy="21" r="20.5" transform="rotate(90 21 21)" fill="white" stroke="#D8D4D7" />
								<path d="M28.0044 19.0735L20.9333 26.1446L13.8623 19.0735" stroke="#92004D" strokeWidth="2" />
							</svg>
						</motion.button>
					</form>
				</Grid.Element>
				{ hasCalculator && calculatorContent && calculatorValues && (
					<Grid.Element type="illustration" position="right" >
						<Calculator
							sliderOptions={ {
								min: 0,
								max: 5000,
								label: calculatorContent.Title,
								body: calculatorContent.Text,
								resultText1: calculatorContent.TextUdbetaling,
								resultText2: calculatorContent.TextProcent
							} }
							doCalculation={ doCalculation }
							isCalculating={ calculatorLoading }
							salary={ calculatorValues.Ydelse }
							procentage={ calculatorValues.Procent }
						/>
					</Grid.Element>
				) }
			</Suspense>
		</MotionGrid>
	);
};

export default Snack;